import { m } from 'framer-motion';
import { useLayoutEffect, useRef } from 'react';
import { SHEET_MOTION } from 'stores/bottomSheetStore';
import useDialogStore from 'stores/dialogStore';
import type { DialogPayload } from 'types/dialog';
import Button from './Button';

const BACKDROP_OPACITY = 0.5;
const CARD_MOTION = { duration: SHEET_MOTION.openDuration, ease: SHEET_MOTION.openEase };

const Dialog: React.FC<{ dialog: DialogPayload }> = ({ dialog }) => {
  const close = useDialogStore((state) => state.close);
  const cancel = useDialogStore((state) => state.dismiss);
  const { id, title, description, confirmText = brsc.confirm, confirmVariant = 'solid', cancelText, cancelPlacement = 'inside', dismissible = true } = dialog;
  const hasInlineActions = !!cancelText && cancelPlacement === 'inside';
  const { confirmColors, cancelColors, onMount } = dialog;
  const contentRef = useRef<HTMLDivElement>(null);
  const fullscreen = dialog.layout === 'fullscreen';
  useLayoutEffect(() => {
    if (contentRef.current) return onMount?.(contentRef.current);
  }, [id, onMount]);
  const actionClassName = hasInlineActions ? 'min-w-fit max-w-full !flex-[1_0_calc(50%_-_0.25rem)] px-4 !max-h-none [overflow-wrap:anywhere]' : 'w-full';

  const dismiss = () => {
    if (dismissible) cancel(id);
  };

  return (
    <div data-component="Dialog" className={`fixed inset-0 z-[110] flex items-center justify-center ${fullscreen ? '' : 'px-6'}`} dir={BResource.isRTL() ? 'rtl' : 'ltr'}>
      <m.div
        className="absolute inset-0 bg-black"
        initial={{ opacity: 0 }}
        animate={{ opacity: dialog.backdropOpacity ?? BACKDROP_OPACITY }}
        exit={{ opacity: 0 }}
        transition={{ duration: SHEET_MOTION.dimDuration, ease: SHEET_MOTION.dimEase }}
        onClick={
          dialog.dismissOnBackdrop === false
            ? undefined
            : dialog.dismissOnBackdrop === 'silent'
              ? () => {
                  if (dismissible) close(id, false);
                }
              : dismiss
        }
      />
      <m.div
        role="dialog"
        aria-modal="true"
        style={{ background: dialog.surfaceColors?.background }}
        className={`relative flex w-full flex-col items-center text-center ${fullscreen ? 'h-full' : 'max-w-[328px] rounded-[24px] bg-dialog-bg-default p-5'} ${dialog.showCloseButton ? 'pt-12' : ''}`}
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.96 }}
        transition={CARD_MOTION}
      >
        {dialog.showCloseButton && (
          <button type="button" aria-label={brsc.close} className="absolute end-2 top-2 flex h-10 w-10 items-center justify-center" onClick={() => cancel(id)}>
            <img src="/images/icon/cancel_grey.png" alt="" className="h-4 w-4" />
          </button>
        )}
        {title && <div className="typography-title-md pt-2 text-dialog-txt-default" style={{ color: dialog.surfaceColors?.text }}>{title}</div>}
        <div
          ref={contentRef}
          style={{ color: dialog.surfaceColors?.text }}
          className={`typography-body-md text-dialog-txt-secondary ${fullscreen ? 'h-full w-full' : dialog.hideActions ? 'w-full py-3' : 'w-full pb-6 pt-3'}`}
          dangerouslySetInnerHTML={{ __html: description }}
        />
        {/* Confirm stays at the logical end of a row, or above cancel when the labels need separate rows. */}
        {!dialog.hideActions && (
          <div className={hasInlineActions ? 'flex w-full flex-row-reverse flex-wrap gap-2' : 'flex w-full flex-col gap-1'}>
            <Button
              onClick={() => close(id, true)}
              style={{
                text: confirmText,
                type: confirmVariant,
                color: 'btn-bg-primary',
                fontColor: confirmVariant === 'text' ? 'btn-txt-only' : 'btn-txt-primary',
                background: confirmColors?.background,
                textColor: confirmColors?.text,
              }}
              className={actionClassName}
            />
            {cancelText && (
              <Button
                onClick={() => cancel(id)}
                style={{
                  text: cancelText,
                  type: cancelPlacement === 'outside' ? 'text' : 'solid',
                  color: 'dialog-bg-default',
                  fontColor: 'btn-txt-only',
                  background: cancelColors?.background,
                  textColor: cancelColors?.text,
                }}
                className={cancelPlacement === 'outside' ? 'absolute inset-x-0 top-full mt-3 w-full' : actionClassName}
              />
            )}
          </div>
        )}
      </m.div>
    </div>
  );
};

export default Dialog;
